import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Modal from '../../../../components/modal/modal';
import '../../../../components/modal/modal.scss';
import { useDispatch } from 'react-redux';
import { getProjectList } from '../../../../actions/project-management.action';
import MultiSelect from '../../../../components/inputs/multi-select';


export const MaterialUsageDialog = ({
    show,
    onHide,
    usageData,
    importedItems,
    onConfirm
}) => {
    const dispatch = useDispatch();
    const [selectedOptions, setSelectedOptions] = useState({
        useItems: true,  // Always true by default
        useItemTemplates: false,
        useProjects: false,
        useSpecificProjects: false,
        useTaskTemplates: false
    });
    const [costedProjects, setCostedProjects] = useState([]);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);

    const [summary, setSummary] = useState({
        newItems: 0,
        itemsToUpdate: 0,
        unchangedItems: 0
    });

    useEffect(() => {
        if (show) {
            dispatch(getProjectList())
                .then(res => {
                    if (res && Array.isArray(res)) {
                        setCostedProjects(res);
                    }
                });
        }
    }, [show, dispatch]);

    useEffect(() => {
        if (usageData && importedItems) {
            const existingPartNos = new Set(usageData.items.map(item => item.part_no));

            // Count new items (items in importedItems but not in usageData)
            const newItemsCount = importedItems.filter(item => !existingPartNos.has(item.partNo)).length;

            // Count items that will be updated (matching part numbers with different costs)
            const itemsToUpdateCount = usageData.items.filter(item => {
                const importedItem = importedItems.find(imp => imp.partNo === item.part_no);
                if (!importedItem) return false;
                return parseFloat(importedItem.cost) !== parseFloat(item.cost);
            }).length;

            // Count unchanged items
            const unchangedItemsCount = usageData.items.length - itemsToUpdateCount;

            setSummary({
                newItems: newItemsCount,
                itemsToUpdate: itemsToUpdateCount,
                unchangedItems: unchangedItemsCount
            });
        }
    }, [usageData, importedItems]);

    const handleOptionChange = (option, value) => {
        setSelectedOptions(prev => {
            const nextOptions = { ...prev, [option]: value };
            if (option === 'useProjects' && value) {
                nextOptions.useSpecificProjects = false;
                setSelectedProjectIds([]);
            } else if (option === 'useSpecificProjects' && value) {
                nextOptions.useProjects = false;
            }
            return nextOptions;
        });
    };

    return (
        <>
            <Modal
                className="material-usage-modal"
                open={show}
                onClose={onHide}
                fullWidth={true}
                maxWidth='sm'
                modalHeading="Material Usage Summary"
                modalSaveTxt='Proceed with Import'
                modalCloseTxt='Cancel'
                onClick={() => onConfirm('update', {
                    ...selectedOptions,
                    updatedItems: usageData.items,
                    selectedProjectIds: selectedOptions.useSpecificProjects ? selectedProjectIds : [],
                    createRevision: selectedOptions.useSpecificProjects && (selectedProjectIds || []).length > 0
                })}
                showActionBtn
                savebtnClass='createBtn'
                cxlbtnClass='cancelBtn'
                primary
            >
                <div className="mb-4">
                    <h6 className="mb-3">Import Summary</h6>
                    <div className="ms-3">
                        <div className="d-flex mb-2">
                            <span>New items to be added:</span>
                            <span className="ms-2 fw-bold">{summary.newItems}</span>
                        </div>
                        <div className="d-flex mb-2">
                            <span>Items to be updated:</span>
                            <span className="ms-2 fw-bold">{summary.itemsToUpdate}</span>
                        </div>
                        <div className="d-flex mb-2">
                            <span>Unchanged items:</span>
                            <span className="ms-2 fw-bold">{summary.unchangedItems}</span>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <h6 className="mb-3">Which of the following do you want the changes to be applied to? Select all that apply:</h6>
                    <div className="ms-3">
                        <Form.Check
                            type="checkbox"
                            label="Item Templates"
                            checked={selectedOptions.useItemTemplates}
                            onChange={(e) => handleOptionChange('useItemTemplates', e.target.checked)}
                            className="mb-2"
                        />
                        <Form.Check
                            type="checkbox"
                            label="Projects(Only applies to projects that are not submitted)"
                            checked={selectedOptions.useProjects}
                            onChange={(e) => handleOptionChange('useProjects', e.target.checked)}
                            className="mb-2"
                        />
                        <Form.Check
                            type="checkbox"
                            label="Specific Projects"
                            checked={selectedOptions.useSpecificProjects}
                            onChange={(e) => handleOptionChange('useSpecificProjects', e.target.checked)}
                            className="mb-2"
                        />
                        {selectedOptions.useSpecificProjects && (
                            <div className="mb-3 mt-2 ms-4">
                                <MultiSelect
                                    placeholder="Select projects..."
                                    isMulti={true}
                                    items={costedProjects.map(p => ({ label: p.name, value: p.id }))}
                                    value={costedProjects
                                        .filter(p => selectedProjectIds.includes(p.id))
                                        .map(p => ({ label: p.name, value: p.id }))
                                    }
                                    onChangeHandler={(selected) => {
                                        setSelectedProjectIds(selected ? selected.map(s => s.value) : []);
                                    }}
                                />
                                {selectedProjectIds.length > 0 && (
                                    <Form.Text className="text-info">
                                        Selected projects will be updated and new revisions created.
                                    </Form.Text>
                                )}
                            </div>
                        )}
                        <Form.Check
                            type="checkbox"
                            label="Task Templates"
                            checked={selectedOptions.useTaskTemplates}
                            onChange={(e) => handleOptionChange('useTaskTemplates', e.target.checked)}
                            className="mb-2"
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
}; 